import { MetricKeysDataPoint, Period } from '@/lib/enums/bi-enums'
import {
	GraphData,
	MetricFrontendDataPoint,
	PerformanceMetricsApiData,
} from '@/app/service/models/performanceMetrics'
import {
	getMonthAbbreviation,
	convertMonthStringToNumber,
	joinArraysAsKeyValuePairs,
} from '@/app/utilities/dateFormatter'
import {
	formatCurrencyWithSignificantDigit,
	formatNumberWithRoundingLevel,
} from '@/app/utilities/numberformatter'
import { Strings } from '@/lib/constants/strings'

const {
	LISTING_STATUS_CLOSED,
	LISTING_STATUS_PENDING,
	CONVERSION_LABEL,
	GCI_LABEL,
	SIDES_LABEL,
	SALES_VOLUME_LABEL,
	OTHER_REVENUE_LABEL,
	OTHER_REVENUE_FOOTER_LABEL,
} = Strings

type CategoryMap<T extends string> = Record<T, string>
interface SeriesMapperTheme {
	palette: {
		dataVisual: {
			spectrum: CategoryMap<'red' | 'green'>
		}
		stroke: {
			neutral: {
				disabledDark: string
			}
		}
	}
}
/**
 * Calculates and returns an array of date-related values based on the given period.
 *
 * @param {Period} period - The period for which to calculate date values. Can be either `Period.YTD` (Year to Date) or another period.
 * @returns {number[]} An array containing the following date values:
 * - currentMonth: The current month (0-11).
 * - currentYear: The current year.
 * - startMonth: The starting month based on the period.
 * - startYear: The starting year based on the period.
 * - compareCurrYear: The current year for comparison purposes.
 * - comparePriorYear: The prior year for comparison purposes.
 */
const getDateVals = (period: Period) => {
	const currentDate = new Date()
	const currentYear = currentDate.getFullYear()
	const currentMonth = currentDate.getMonth()

	const startMonth = period === Period.YTD ? 0 : currentMonth
	const startYear = period === Period.YTD ? currentYear : currentYear - 1

	const compareCurrYear = currentYear
	const comparePriorYear = currentYear - 1

	return [
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		compareCurrYear,
		comparePriorYear,
	]
}
export function fillMissingMonthsInMetrics(
	metrics: MetricFrontendDataPoint[],
	startYear: number,
	startMonth: number,
	endYear?: number
): MetricFrontendDataPoint[] {
	if (!metrics.length) return []

	const filled: MetricFrontendDataPoint[] = []
	const metricType = metrics[0].metricTypeCd

	// Determine end year/month
	const lastMetric = metrics[metrics.length - 1]
	const lastDate = new Date(lastMetric.metricStartDt + 'T00:00:00')
	const finalYear = endYear ?? lastDate.getFullYear()
	const finalMonth = 11

	let year = startYear
	let month = startMonth
	let seq = 0
	let metricIdx = 0

	while (year < finalYear || (year === finalYear && month <= finalMonth)) {
		const expectedDateStr = `${year}-${String(month + 1).padStart(2, '0')}-01`
		const metric = metrics[metricIdx]

		if (metric && metric.metricStartDt === expectedDateStr) {
			filled.push({ ...metric, sequenceNo: seq })
			metricIdx++
		} else {
			// Fill blank metric for missing month
			filled.push({
				metricTypeCd: metricType,
				metricStartDt: expectedDateStr,
				closedNo: 0,
				pendingNo: 0,
				openNo: 0,
				otherRevenueClosedNo: 0,
				prevClosedNo: 0,
				prevPendingNo: 0,
				prevOpenNo: 0,
				prevOtherRevenueClosedNo: 0,
				sequenceNo: seq,
				benchmarkClosedNo: 0,
				benchmarkOpenNo: 0,
				totalBenchmarkOpenSides: 0,
				totalBenchmarkClosedSides: 0,
				totalBenchmarkOpenSalesVolume: 0,
				totalBenchmarkClosedSalesVolume: 0,
			})
		}
		seq++
		if (month === 11) {
			month = 0
			year++
		} else {
			month++
		}
	}
	return filled
}
/**
 * Retrieves the most recent data point from an array of MetricFrontendDataPoint objects.
 *
 * @param {MetricFrontendDataPoint[]} dataPoints - An array of data points to evaluate.
 * @returns {MetricFrontendDataPoint} The most recent data point based on the metricStartDt property.
 */
export const getMostRecent = (dataPoints: MetricFrontendDataPoint[]) => {
	return dataPoints.reduce((mostRecent, current) => {
		return (mostRecent.metricStartDt ?? '') > (current.metricStartDt ?? '')
			? mostRecent
			: current
	}, dataPoints[0])
}

export const padMonthsWithZero = (
	metrics: MetricFrontendDataPoint[],
	startMonth: number,
	startYear: number
) => {
	if (metrics.length === 0) return metrics
	const firstExistingYear = metrics[0].metricStartDt
		? new Date(metrics[0].metricStartDt).getFullYear()
		: 0
	const firstExistingMonth = metrics[0].metricStartDt
		? new Date(metrics[0].metricStartDt).getMonth()
		: 0

	const zeroFilledData: MetricFrontendDataPoint[] = []
	const metricType = metrics[0].metricTypeCd
	let padYear = startYear
	let padMonth = startMonth
	let sequenceNo = 0
	while (
		padYear < firstExistingYear ||
		(padYear === firstExistingYear && padMonth < firstExistingMonth)
	) {
		zeroFilledData.push({
			metricTypeCd: metricType,
			metricStartDt: `${padYear}-${String(padMonth + 1).padStart(2, '0')}-01`,
			closedNo: 0,
			pendingNo: 0,
			openNo: 0,
			otherRevenueClosedNo: 0,
			prevClosedNo: 0,
			prevPendingNo: 0,
			prevOpenNo: 0,
			prevOtherRevenueClosedNo: 0,
			sequenceNo,
			benchmarkClosedNo: 0,
			benchmarkOpenNo: 0,
			totalBenchmarkOpenSides: 0,
			totalBenchmarkClosedSides: 0,
			totalBenchmarkOpenSalesVolume: 0,
			totalBenchmarkClosedSalesVolume: 0,
		})
		sequenceNo++
		padMonth++
		if (padMonth === 12) {
			padMonth = 0
			padYear++
		}
	}

	const startMetricYear = metrics[0].metricStartDt
		? new Date(metrics[0].metricStartDt).getFullYear()
		: 0
	const strartMetricMonth = metrics[0].metricStartDt
		? new Date(metrics[0].metricStartDt).getMonth()
		: 0
	const filledMetrics = fillMissingMonthsInMetrics(
		metrics,
		startMetricYear,
		strartMetricMonth
	)
	const paddedMetrics = zeroFilledData.concat(
		filledMetrics.map(metric => {
			const updatedMetric = { ...metric }
			updatedMetric.sequenceNo = sequenceNo
			sequenceNo++
			return updatedMetric
		})
	)
	return paddedMetrics
}

/**
 * Filters and sorts an array of metrics based on the specified period and date range.
 *
 * @param metrics - An array of MetricFrontendDataPoint objects to be filtered and sorted.
 * @param period - The period to filter the metrics by (YTD, LTM, COMPARE).
 * @param currentMonth - The current month (0-based index).
 * @param currentYear - The current year.
 * @param startMonth - The start month (0-based index) for the LTM period.
 * @param startYear - The start year for the LTM period.
 * @returns The filtered and sorted array of metrics.
 */
const filterAndSortByDateSingle = (
	metrics: MetricFrontendDataPoint[],
	period: Period,
	currentMonth: number,
	currentYear: number,
	startMonth: number,
	startYear: number,
	preview2LayoutFlag: boolean = true
) => {
	metrics.sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
	const paddingStartYear =
		period === Period.YTD || period === Period.LTM ? startYear : startYear - 1
	const paddedMetrics = padMonthsWithZero(metrics, startMonth, paddingStartYear)
	const filteredSorted = paddedMetrics
		.filter(metric => {
			const metricDate = new Date((metric.metricStartDt ?? '') + 'T00:00:00')
			const metricYear = metricDate.getFullYear()
			const metricMonth = metricDate.getMonth()

			switch (period) {
				case Period.YTD:
					if (preview2LayoutFlag) {
						return metricYear === currentYear
					}
					return metricYear === currentYear && metricMonth <= currentMonth
				case Period.LTM:
					return (
						(metricYear === startYear && metricMonth >= startMonth) ||
						(metricYear === currentYear && metricMonth < currentMonth)
					)
				case Period.PY:
					return metricYear === currentYear - 1
				default:
					return metricYear === currentYear && metricMonth <= currentMonth
			}
		})
		.sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
	return filteredSorted
}

/**
 * Filters and sorts performance metrics data by date for the given period.
 *
 * @param {PerformanceMetricsApiData} metricsData - The performance metrics data to be filtered and sorted.
 * @param {Period} period - The period for which the data should be filtered and sorted.
 * @returns {[
 *   MetricFrontendDataPoint[],
 *   MetricFrontendDataPoint[],
 *   MetricFrontendDataPoint[],
 *   MetricFrontendDataPoint[],
 *   MetricFrontendDataPoint[],
 *   MetricFrontendDataPoint[]
 * ]} An array containing six arrays of MetricFrontendDataPoint:
 *   - gciData: Filtered and sorted GCI metrics for the current period.
 *   - sidesData: Filtered and sorted sides metrics for the current period.
 *   - salesVolumeData: Filtered and sorted sales volume metrics for the current period.
 *   - compareGciData: Filtered and sorted GCI metrics for the comparison period.
 *   - compareSideData: Filtered and sorted sides metrics for the comparison period.
 *   - compareSalesVolumeData: Filtered and sorted sales volume metrics for the comparison period.
 */
export const filterAndSortAllByDate = (
	metricsData: PerformanceMetricsApiData,
	period: Period,
	preview2LayoutFlag: boolean = true
): [
	MetricFrontendDataPoint[],
	MetricFrontendDataPoint[],
	MetricFrontendDataPoint[],
	MetricFrontendDataPoint[],
	MetricFrontendDataPoint[],
	MetricFrontendDataPoint[],
] => {
	const [
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		compareCurrYear,
		comparePriorYear,
	] = getDateVals(period)
	const gciData = filterAndSortByDateSingle(
		metricsData.gciMetrics,
		period,
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		preview2LayoutFlag
	)
	const sidesData = filterAndSortByDateSingle(
		metricsData.sidesMetrics,
		period,
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		preview2LayoutFlag
	)
	const salesVolumeData = filterAndSortByDateSingle(
		metricsData.salesVolumeMetrics,
		period,
		currentMonth,
		currentYear,
		startMonth,
		startYear,
		preview2LayoutFlag
	)
	const compareGciData = filterAndSortByDateSingle(
		metricsData.gciMetrics,
		period,
		currentMonth,
		compareCurrYear,
		startMonth,
		comparePriorYear,
		preview2LayoutFlag
	)
	const compareSideData = filterAndSortByDateSingle(
		metricsData.sidesMetrics,
		period,
		currentMonth,
		compareCurrYear,
		startMonth,
		comparePriorYear,
		preview2LayoutFlag
	)
	const compareSalesVolumeData = filterAndSortByDateSingle(
		metricsData.salesVolumeMetrics,
		period,
		currentMonth,
		compareCurrYear,
		startMonth,
		comparePriorYear,
		preview2LayoutFlag
	)
	return [
		gciData,
		sidesData,
		salesVolumeData,
		compareGciData,
		compareSideData,
		compareSalesVolumeData,
	]
}

/**
 * Calculates the year-over-year percentage change between the current and previous values.
 *
 * @param current - The current year's value.
 * @param previous - The previous year's value.
 * @returns The percentage change from the previous year to the current year, or null if the previous year's value is zero.
 */
export const yearOverYearCalc = (current: number, previous: number) => {
	return previous !== 0 ? ((current - previous) / previous) * 100 : null
}

/**
 * Extracts a specific numeric property from an array of metric data points.
 *
 * @param data - Array of metric data points to process
 * @param key - The property key to extract from each data point
 * @param roundOffValue - Whether to round the extracted values to nearest integer (defaults to false)
 * @returns An array of numbers extracted from the specified property of each data point
 *
 * @example
 * // Returns [10, 20, 30]
 * dataArrayFormat(metricData, 'value')
 *
 * @example
 * // Returns [10, 20, 30] with rounded values
 * dataArrayFormat(metricData, 'value', true)
 */
const dataArrayFormat = (
	data: MetricFrontendDataPoint[],
	key: keyof MetricFrontendDataPoint,
	roundOffValue: boolean = false
): number[] => {
	return data?.map(metric => {
		const value = metric[key] as number | undefined
		return roundOffValue ? Math.round(value ?? 0) : (value ?? 0)
	})
}

/**
 * Organizes metric data points by their status and performs optional rounding.
 *
 * @param data - Array of metric data points.
 * @param currentMonth - The current month (0-11).
 * @param currentYear - The current year.
 * @param compareYear - The previous year for comparison chart.
 * @param allPendingVals - Optional. If true, includes all pending values regardless of date. Defaults to false.
 * @param roundOffValue - Optional. If true, rounds off the values. Defaults to false.
 * @returns An object containing arrays of organized metric values:
 * - `openValues`: Array of open values.
 * - `closedValues`: Array of closed values.
 * - `pendingValues`: Array of pending values, filtered by date if `allPendingVals` is false.
 * - `benchmarkClosedNo`: Array of fictional benchmark closed numbers.
 * - `comparePriorClosedNo`: Array of prior closed numbers.
 * - `compareCurrentClosedNo`: Array of current closed numbers.
 * - `comparePriorOpenNo`: Array of prior open numbers.
 * - `compareCurrentOpenNo`: Array of current open numbers.
 */

const organizeByStatus = (
	data: MetricFrontendDataPoint[],
	currentMonth: number,
	currentYear: number,
	compareData?: MetricFrontendDataPoint[],
	allPendingVals: boolean = true, // set to true for dashboard preview 2.0 layout
	roundOffValue: boolean = false,
	otherRevenueClosed: boolean = false
) => ({
	openValues: dataArrayFormat(data, MetricKeysDataPoint.openNo, roundOffValue),
	closedValues: dataArrayFormat(
		data,
		MetricKeysDataPoint.closedNo,
		roundOffValue
	),
	...(allPendingVals &&
		otherRevenueClosed && {
			otherRevenueClosedValues: dataArrayFormat(
				data,
				MetricKeysDataPoint.otherRevenueClosedNo,
				roundOffValue
			),
		}),
	pendingValues: data.map(metric => {
		const metricDate = new Date((metric.metricStartDt ?? '') + 'T00:00:00')
		if (
			allPendingVals ||
			(currentMonth === metricDate.getMonth() &&
				currentYear === metricDate.getFullYear())
		) {
			return roundOffValue
				? Math.round(metric?.pendingNo ?? 0)
				: (metric?.pendingNo ?? 0)
		}
		return 0
	}),
	compareBenchmarkClosedNo: dataArrayFormat(
		data,
		MetricKeysDataPoint.benchmarkClosedNo,
		roundOffValue
	),
	compareBenchmarkOpenNo: dataArrayFormat(
		data,
		MetricKeysDataPoint.benchmarkOpenNo,
		roundOffValue
	),
	comparePriorClosedNo: dataArrayFormat(
		compareData as MetricFrontendDataPoint[],
		MetricKeysDataPoint.prevClosedNo,
		roundOffValue
	),
	compareCurrentClosedNo: dataArrayFormat(
		data,
		MetricKeysDataPoint.closedNo,
		roundOffValue
	),
	comparePriorOpenNo: dataArrayFormat(
		compareData as MetricFrontendDataPoint[],
		MetricKeysDataPoint.prevOpenNo,
		roundOffValue
	),
	compareCurrentOpenNo: dataArrayFormat(
		data,
		MetricKeysDataPoint.openNo,
		roundOffValue
	),
})

const formatPendingYTD = (
	pendingValues: number[],
	monthListYTD: string[]
): number[] => {
	const pendingValueYTD: number[] = Array(12).fill(0)
	const monthValuePairs = joinArraysAsKeyValuePairs(monthListYTD, pendingValues)
	for (const key in monthValuePairs) {
		if (Object.hasOwn(monthValuePairs, key)) {
			const monthNumber = convertMonthStringToNumber(key)
			if (monthNumber !== undefined) {
				pendingValueYTD[monthNumber - 1] = monthValuePairs[key]
			}
		}
	}
	return pendingValueYTD
}

/**
 * Generates graph data for both Year-To-Date (YTD) and Last Twelve Months (LTM) periods
 * from the provided performance metrics data.
 *
 * @param {PerformanceMetricsApiData} metricsData - The performance metrics data.
 * @returns {GraphData} The organized graph data for YTD and LTM periods.
 */
export const createGraphData = (
	metricsData: PerformanceMetricsApiData
): GraphData => {
	const preview2LayoutFlag: boolean = true
	const [currentMonth, currentYear] = getDateVals(Period.YTD)
	const [
		gciDataYTD,
		sidesDataYTD,
		salesVolumeDataYTD,
		compareGciDataYTD,
		compareSideDataYTD,
		compareSalesVolumeDataYTD,
	] = filterAndSortAllByDate(metricsData, Period.YTD, preview2LayoutFlag)
	const [
		gciDataLTM,
		sidesDataLTM,
		salesVolumeDataLTM,
		compareGciDataLTM,
		compareSideDataLTM,
		compareSalesVolumeDataLTM,
	] = filterAndSortAllByDate(metricsData, Period.LTM, preview2LayoutFlag)

	const [
		gciDataPY,
		sidesDataPY,
		salesVolumeDataPY,
		compareGciDataPY,
		compareSideDataPY,
		compareSalesVolumeDataPY,
	] = filterAndSortAllByDate(metricsData, Period.PY, preview2LayoutFlag)
	const gciDataFormatted: ReturnType<typeof organizeByStatus> =
		organizeByStatus(
			gciDataYTD,
			currentMonth,
			currentYear,
			compareGciDataYTD,
			preview2LayoutFlag,
			false,
			true
		)
	const sidesDataFormatted: ReturnType<typeof organizeByStatus> =
		organizeByStatus(
			sidesDataYTD,
			currentMonth,
			currentYear,
			compareSideDataYTD,
			preview2LayoutFlag,
			true
		)
	const salesVolumeDataFormatted: ReturnType<typeof organizeByStatus> =
		organizeByStatus(
			salesVolumeDataYTD,
			currentMonth,
			currentYear,
			compareSalesVolumeDataYTD,
			preview2LayoutFlag
		)
	const monthListYTD = gciDataYTD.map(metric =>
		getMonthAbbreviation(metric.metricStartDt ?? '')
	)
	gciDataFormatted.pendingValues = preview2LayoutFlag
		? formatPendingYTD(gciDataFormatted.pendingValues, monthListYTD)
		: gciDataFormatted.pendingValues
	sidesDataFormatted.pendingValues = preview2LayoutFlag
		? formatPendingYTD(sidesDataFormatted.pendingValues, monthListYTD)
		: sidesDataFormatted.pendingValues
	salesVolumeDataFormatted.pendingValues = preview2LayoutFlag
		? formatPendingYTD(salesVolumeDataFormatted.pendingValues, monthListYTD)
		: salesVolumeDataFormatted.pendingValues
	return {
		ytd: {
			gci: gciDataFormatted,
			sides: sidesDataFormatted,
			salesVolume: salesVolumeDataFormatted,
			months: gciDataYTD.map(metric =>
				getMonthAbbreviation(metric.metricStartDt ?? '')
			),
			updatedTime: metricsData.updateTs ?? '',
		},
		ltm: {
			gci: organizeByStatus(
				gciDataLTM,
				currentMonth,
				currentYear,
				compareGciDataLTM,
				preview2LayoutFlag,
				false,
				true
			),
			sides: organizeByStatus(
				sidesDataLTM,
				currentMonth,
				currentYear,
				compareSideDataLTM,
				preview2LayoutFlag,
				true
			),
			salesVolume: organizeByStatus(
				salesVolumeDataLTM,
				currentMonth,
				currentYear,
				compareSalesVolumeDataLTM,
				preview2LayoutFlag
			),
			months: gciDataLTM.map(metric =>
				getMonthAbbreviation(metric.metricStartDt ?? '')
			),
			updatedTime: metricsData.updateTs ?? '',
		},
		month: {
			gci: organizeByStatus(
				gciDataPY,
				currentMonth,
				currentYear,
				compareGciDataPY,
				preview2LayoutFlag,
				false,
				true
			),
			sides: organizeByStatus(
				sidesDataPY,
				currentMonth,
				currentYear,
				compareSideDataPY,
				preview2LayoutFlag,
				true
			),
			salesVolume: organizeByStatus(
				salesVolumeDataPY,
				currentMonth,
				currentYear,
				compareSalesVolumeDataPY,
				preview2LayoutFlag
			),
			months: gciDataPY.map(metric =>
				getMonthAbbreviation(metric.metricStartDt ?? '')
			),
			updatedTime: metricsData.updateTs ?? '',
		},
	}
}

/**
 * Generates tile data for GCI, sides, and sales volume metrics.
 *
 * @param metricsData - The performance metrics data from the API.
 * @returns An object containing the calculated cumulative data for GCI, sides, and sales volume.
 *
 * The returned object has the following structure:
 * - `gci`: Contains cumulative data for GCI metrics.
 * - `sides`: Contains cumulative data for sides metrics.
 * - `salesVolume`: Contains cumulative data for sales volume metrics.
 *
 * Each of these properties is an object with the following structure:
 * - `closedTransactions`: An array of objects representing closed transactions data.
 *   - `header`: The metric type.
 *   - `content`: The formatted cumulative value.
 *   - `footer`: The formatted pending value.
 * - `openedTransactions`: An array of objects representing opened transactions data.
 *   - `header`: The metric type.
 *   - `content`: The formatted cumulative value.
 *   - `footer`: The formatted closed conversion percentage and label.
 *   - `footerIcon`: The icon to be displayed in the footer.
 */

export const createTilesData = (
	metricsData: PerformanceMetricsApiData,
	theme: SeriesMapperTheme
) => {
	const getYoyPercentDirectionForOtherRevenue = (yoyPercent: number) => {
		if (yoyPercent > 0.5) return 'up'
		if (yoyPercent < -0.5) return 'down'
		return 'flat'
	}

	const generateYoyColor = (yoy: number): string => {
		if (yoy > 0.5) return theme.palette.dataVisual.spectrum.green
		if (yoy < -0.5) return theme.palette.dataVisual.spectrum.red
		return theme.palette.stroke.neutral.disabledDark
	}

	const generateContentBasedOnNotation = (
		value: number,
		significantDigit: number,
		notation: 'percentage' | 'currency' | 'hash' | 'compact'
	) => {
		if (notation === 'compact') {
			return formatCurrencyWithSignificantDigit(
				value,
				significantDigit,
				notation
			)
		}
		return formatNumberWithRoundingLevel(value, significantDigit, notation)
	}

	const generateOtherRevenue = (
		label: string,
		cumulative: number,
		yoy: number,
		notation: 'percentage' | 'currency' | 'hash' | 'compact',
		significantDigit: number
	) => {
		return {
			header: label,
			content: generateContentBasedOnNotation(
				Math.abs(cumulative),
				significantDigit,
				notation
			),
			footer: `${formatNumberWithRoundingLevel(Math.abs(yoy), -1, 'percentage')} ${OTHER_REVENUE_FOOTER_LABEL}`,
			footerIcon: `${getYoyPercentDirectionForOtherRevenue(yoy)}`,
			color: generateYoyColor(yoy),
		}
	}

	const calculateCumulatives = (
		metrics: MetricFrontendDataPoint[],
		currency: boolean,
		biMetricType: string,
		isYTD: boolean = false
	) => {
		const currentMonth = new Date().getMonth()
		const TOTAL_MONTHS = 12
		const sliceEndIndex = isYTD ? currentMonth + 1 : TOTAL_MONTHS
		const closedCumulative = metrics.reduce(
			(sum, metric) => sum + (metric.closedNo ?? 0),
			0
		)
		const otherRevenueCumulative = metrics.reduce(
			(sum, metric) => sum + (metric.otherRevenueClosedNo ?? 0),
			0
		)
		const pendingCurrent = metrics.reduce(
			(sum, metric) => sum + (metric.pendingNo ?? 0),
			0
		)
		const openCumulative = metrics.reduce(
			(sum, metric) => sum + (metric.openNo ?? 0),
			0
		)
		const closedPrevCumulative = metrics
			.slice(0, sliceEndIndex)
			.reduce((sum, metric) => sum + (metric.prevClosedNo ?? 0), 0)
		const openPrevCumulative = metrics
			.slice(0, sliceEndIndex)
			.reduce((sum, metric) => sum + (metric.prevOpenNo ?? 0), 0)
		const otherRevenuePrevCumulative = metrics
			.slice(0, sliceEndIndex)
			.reduce((sum, metric) => sum + (metric.prevOtherRevenueClosedNo ?? 0), 0)
		const closedConversionRate = (closedCumulative / openCumulative) * 100
		const closedConversion =
			isNaN(closedConversionRate) || closedConversionRate === Infinity
				? 0
				: closedConversionRate
		const yoyClosed = yearOverYearCalc(closedCumulative, closedPrevCumulative)
		const yoyOpen = yearOverYearCalc(openCumulative, openPrevCumulative)
		const yoyOtherRevenue = yearOverYearCalc(
			otherRevenueCumulative,
			otherRevenuePrevCumulative
		)
		const notationType = biMetricType === 'Sides' ? 'hash' : 'currency'
		const significantDigits = biMetricType === 'Sides' ? 0 : 3
		const closedData = generateOtherRevenue(
			biMetricType,
			closedCumulative,
			yoyClosed ?? 0,
			notationType,
			significantDigits
		)
		const pendingData = generateOtherRevenue(
			`${LISTING_STATUS_PENDING} ${biMetricType}`,
			pendingCurrent,
			0,
			notationType,
			significantDigits
		)
		const openedData = generateOtherRevenue(
			biMetricType,
			openCumulative,
			yoyOpen ?? 0,
			notationType,
			significantDigits
		)
		const closedConversionPercentage = generateOtherRevenue(
			`${LISTING_STATUS_CLOSED} ${CONVERSION_LABEL}`,
			closedConversion,
			0,
			'percentage',
			-1
		)
		return {
			closedTransactions: [{ ...closedData }, { ...pendingData }],
			openedTransactions: [
				{ ...openedData },
				{ ...closedConversionPercentage },
			],
			...(biMetricType === GCI_LABEL && {
				otherRevenueTransactions: generateOtherRevenue(
					OTHER_REVENUE_LABEL,
					otherRevenueCumulative,
					yoyOtherRevenue ?? 0,
					'compact',
					3
				),
			}),
		}
	}

	const [gciDataYTD, sidesDataYTD, salesVolumeDataYTD] = filterAndSortAllByDate(
		metricsData,
		Period.YTD
	)
	const [gciDataLTM, sidesDataLTM, salesVolumeDataLTM] = filterAndSortAllByDate(
		metricsData,
		Period.LTM
	)
	const [gciDataPY, sidesDataPY, salesVolumeDataPY] = filterAndSortAllByDate(
		metricsData,
		Period.PY
	)

	return {
		ytd: {
			gci: calculateCumulatives(gciDataYTD, true, GCI_LABEL, true),
			sides: calculateCumulatives(sidesDataYTD, false, SIDES_LABEL, true),
			salesVolume: calculateCumulatives(
				salesVolumeDataYTD,
				true,
				SALES_VOLUME_LABEL,
				true
			),
		},
		ltm: {
			gci: calculateCumulatives(gciDataLTM, true, GCI_LABEL),
			sides: calculateCumulatives(sidesDataLTM, false, SIDES_LABEL),
			salesVolume: calculateCumulatives(
				salesVolumeDataLTM,
				true,
				SALES_VOLUME_LABEL
			),
		},
		month: {
			gci: calculateCumulatives(gciDataPY, true, GCI_LABEL),
			sides: calculateCumulatives(sidesDataPY, false, SIDES_LABEL),
			salesVolume: calculateCumulatives(
				salesVolumeDataPY,
				true,
				SALES_VOLUME_LABEL
			),
		},
	}
}
